import { Component, OnInit, inject } from '@angular/core';
import { DatePipe } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import {
	ItBreadcrumbComponent,
	ItBreadcrumbItemComponent,
} from 'design-angular-kit';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { IterlexService } from '../iterlex.service';
import { HistogramComponent } from '../histogram/histogram.component';
import type { Authority } from '../interfaces/authority';
import type { Dossier } from '../interfaces/dossier';
import type { DossierType } from '../interfaces/dossier-type';
import type { Phase } from '../interfaces/phase';
import type { HistogramData } from '../interfaces/histogram-data';

@Component({
	selector: 'app-dossier-stats-page',
	imports: [
		FormsModule,
		ItBreadcrumbComponent,
		ItBreadcrumbItemComponent,
		TranslateModule,
		RouterLink,
		DatePipe,
		HistogramComponent,
	],
	templateUrl: './dossier-stats-page.component.html',
	styleUrl: './dossier-stats-page.component.scss',
})
export class DossierStatsPageComponent implements OnInit {
	private activatedRoute = inject(ActivatedRoute);
	private iterlexService = inject(IterlexService);
	private translateService = inject(TranslateService);

	amendmentTypes = [
		'repeal',
		'substitution',
		'insertion',
		'join',
		'split',
		'renumbering',
		'modification',
	];
	authority?: Authority;
	dataAmendmentsPerType: HistogramData[] = [];
	dataAmendmentsPerArticle: HistogramData[] = [];
	body?: Authority;
	dossier: Dossier | null = null;
	phases!: Phase[];
	dossierType?: DossierType;
	ngOnInit(): void {
		const dossierUri =
			this.activatedRoute.snapshot.queryParamMap.get('uri');
		if (dossierUri) {
			const dossierAuthor = dossierUri.split('/')[5];
			this.iterlexService.getAuthorities().subscribe((authorities) => {
				for (const authority of authorities) {
					if (authority.id == dossierAuthor) {
						this.body = authority;
						this.authority = authority;
						break;
					} else if (authority.children.length > 0) {
						this.body = authority.children.find(
							(authority) => authority.id == dossierAuthor,
						);
						if (this.body) {
							this.authority = authority;
							break;
						}
					}
				}
				if (this.authority && this.body) {
					this.iterlexService
						.getDossier(this.authority.id, this.body.id, dossierUri)
						.subscribe((dossier) => {
							this.dossier = dossier;
							this.iterlexService
								.getDossierTypes(
									new HttpParams()
										.append('authority', this.authority!.id)
										.append('body', this.body!.id),
								)
								.subscribe((dossierTypes) => {
									this.dossierType = dossierTypes.find(
										(e) => e.id == dossier.type,
									);
								});
							this.iterlexService
								.getPhases(
									this.authority!.id,
									this.body!.id,
									dossier.type,
								)
								.subscribe((phases) => {
									this.phases = phases;
									const amendmentsPhase = phases.find(
										(phase) => phase.type == 'emendamento',
									);
									if (amendmentsPhase) {
										this.amendmentsStats(amendmentsPhase);
									}
								});
						});
				}
			});
		}
	}
	amendmentsStats(amendmentsPhase: Phase) {
		this.iterlexService
			.getSteps(
				this.authority!.id,
				this.body!.id,
				this.dossier!.workUri,
				amendmentsPhase.id,
			)
			.subscribe((steps) => {
				for (const step of steps) {
					this.iterlexService
						.getDocuments(
							new HttpParams()
								.append('dossierUri', this.dossier!.workUri)
								.append('authority', this.authority!.id)
								.append('body', this.body!.id)
								.append('phaseName', amendmentsPhase.id)
								.append('stepId', step.id),
						)
						.subscribe(async (documents) => {
							const documentsComplete = [];
							for (const document of documents.value) {
								documentsComplete.push(
									await firstValueFrom(
										this.iterlexService.getDocumentComplete(
											this.authority!.id,
											this.body!.id,
											document.workUri,
											amendmentsPhase.id,
											step.id,
										),
									),
								);
							}
							const amendmentsPerType: Record<string, number> =
								{};
							const amendmentsPerArticle: Record<string, number> =
								{};
							for (const amendmentType of this.amendmentTypes) {
								amendmentsPerType[amendmentType] = 0;
							}
							for (const document of documentsComplete) {
								amendmentsPerType[
									document.versions[
										document.versions.length - 1
									].metadata.amendmentType!
								] += 1;
								const amendmentArticle =
									document.versions[
										document.versions.length - 1
									].metadata.article!;
								amendmentsPerArticle[amendmentArticle] =
									(amendmentsPerArticle[amendmentArticle] ??
										0) + 1;
							}
							this.dataAmendmentsPerType = Object.entries(
								amendmentsPerType,
							).map(([k, v]) => ({
								label: this.translateService.instant(
									'iterlex.amendment-' + k,
								),
								value: v,
							}));
							this.dataAmendmentsPerArticle = Object.entries(
								amendmentsPerArticle,
							).map(([k, v]) => ({
								label: k,
								value: v,
							}));
						});
				}
			});
	}
}
